import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, CalendarDays } from "lucide-react";
import { BRAND } from "../lib/brand";
import { useT, LanguageToggle } from "../lib/i18n";

export default function Navbar({ onOpenBookText }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useT();

  const NAV_ITEMS = [
    { key: "home", label: t("nav.home"), href: "#top" },
    { key: "fleet", label: t("nav.fleet"), href: "#fleet" },
    { key: "tours", label: t("nav.tours"), href: "#tours" },
    { key: "locations", label: t("nav.locations"), href: "#locations" },
    { key: "reviews", label: t("nav.reviews"), href: "#reviews" },
    { key: "waiver", label: t("nav.waiver"), href: "/waiver" },
    { key: "contact", label: t("nav.contact"), href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-[background,border,backdrop-filter] duration-300 ${
          scrolled
            ? "bg-black/85 backdrop-blur-md border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        data-testid="site-navbar"
      >
        <div className="mx-auto max-w-[1600px] px-4 md:px-8 lg:px-10 flex items-center justify-between h-14 md:h-16">
          <a href="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="bg-black rounded-full p-0.5 ring-1 ring-white/10">
              <img
                src={BRAND.logo}
                alt="Rockstar ATV Rentals"
                className="h-8 w-8 object-cover rounded-full"
              />
            </div>
            <div>
              <div className="font-display text-white text-sm leading-none tracking-wide">ROCKSTAR</div>
              <div className="font-mono text-[9px] tracking-[0.28em] text-[color:var(--primary)] leading-none mt-0.5">
                ATV RENTALS · FL
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.href}
                className="link-under text-[11px] font-semibold tracking-[0.2em] uppercase text-white/90 hover:text-[color:var(--primary)] transition-colors"
                data-testid={`nav-link-${item.key}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageToggle />
            <a
              href={BRAND.bookingUrl}
              className="hidden md:inline-flex items-center gap-1.5 border border-white/25 hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.16em] text-[10px] px-3.5 py-2.5 transition-colors"
              data-testid="nav-book-now-btn"
            >
              <CalendarDays size={12} /> {t("nav.bookNow")}
            </a>
            <button
              onClick={onOpenBookText}
              className="hidden md:inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[10px] px-3.5 py-2.5 transition-colors"
              data-testid="nav-book-text-btn"
            >
              {t("nav.bookViaText")} <ArrowRight size={12} />
            </button>
            <button
              className="lg:hidden -mr-2 p-3 text-white touch-manipulation"
              onClick={() => setOpen(true)}
              aria-label={t("nav.openMenu")}
              data-testid="nav-mobile-open"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black flex flex-col"
            style={{
              paddingTop: "env(safe-area-inset-top, 0px)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-14 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="bg-black rounded-full p-0.5 ring-1 ring-white/10">
                  <img src={BRAND.logo} alt="logo" className="h-8 w-8 rounded-full" />
                </div>
                <span className="font-display text-white text-sm">ROCKSTAR ATVS</span>
              </div>
              <div className="flex items-center gap-2">
                <LanguageToggle />
                <button
                  onClick={() => setOpen(false)}
                  aria-label={t("nav.close")}
                  data-testid="nav-mobile-close"
                  className="text-white p-2"
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-5">
              {NAV_ITEMS.map((item, idx) => (
                <motion.a
                  key={item.key}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * idx, duration: 0.4 }}
                  className="font-display text-white text-3xl uppercase hover:text-[color:var(--primary)] transition-colors"
                  data-testid={`nav-mobile-link-${item.key}`}
                >
                  {item.label}
                </motion.a>
              ))}
              <div className="mt-4 flex flex-col gap-3 self-start">
                <a
                  href={BRAND.bookingUrl}
                  onClick={() => setOpen(false)}
                  className="border border-white/25 text-white font-bold uppercase tracking-[0.16em] text-xs px-5 py-3 inline-flex items-center gap-2"
                  data-testid="nav-mobile-book-now"
                >
                  <CalendarDays size={14} /> {t("nav.bookNow")}
                </a>
                <button
                  onClick={() => {
                    setOpen(false);
                    onOpenBookText?.();
                  }}
                  className="bg-[color:var(--primary)] text-white font-bold uppercase tracking-[0.16em] text-xs px-5 py-3 inline-flex items-center gap-2"
                  data-testid="nav-mobile-book"
                >
                  {t("nav.bookViaText")} <ArrowRight size={14} />
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
